import { useCallback, useEffect, useRef, useState } from 'react';
import { readCrmAccount, requestAccountReview, syncCrmFromProvider, type CrmAccount } from './crm-account';

/** CRM dossier + explicit review command for one account page. Opening the
 * page only reads; the Kernel review starts on the user's click. */
export const useCrmAccount = (subjectId: string | null, onReviewRequested: () => void) => {
  const [account, setAccount] = useState<CrmAccount | null>(null);
  // Read once: until then « not a CRM account » cannot be told from « not read yet ».
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const current = useRef(subjectId);
  current.current = subjectId;

  useEffect(() => {
    let active = true;
    setAccount(null); setError(null); setReviewError(null); setLoaded(false);
    if (!subjectId) return;
    void readCrmAccount(subjectId)
      .then((value) => { if (active) setAccount(value); })
      .catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : 'Les informations HubSpot n’ont pas pu être chargées.'); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [subjectId, reloadKey]);

  const requestReview = useCallback(() => {
    if (!subjectId) return;
    setReviewing(true); setReviewError(null);
    void requestAccountReview(subjectId, crypto.randomUUID())
      .then(() => { if (current.current === subjectId) onReviewRequested(); })
      .catch((cause: unknown) => {
        if (current.current === subjectId) setReviewError(cause instanceof Error ? cause.message : 'L’examen n’a pas pu être demandé.');
      })
      .finally(() => { if (current.current === subjectId) setReviewing(false); });
  }, [subjectId, onReviewRequested]);

  const syncFromCrm = useCallback(() => {
    setSyncing(true); setError(null);
    void syncCrmFromProvider()
      .then(() => setReloadKey((key) => key + 1))
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'HubSpot n’a pas pu être relu.'))
      .finally(() => setSyncing(false));
  }, []);

  return { account, loaded, error, reload: () => setReloadKey((key) => key + 1), reviewing, reviewError, requestReview, syncing, syncFromCrm };
};
